import React, { useEffect, useRef, useState } from 'react';
import { API_CONFIG } from '../config/api.config';
import { useTranslation } from '../i18n';

interface GoogleSignInButtonProps {
  onCredential: (credential: string) => Promise<void>;
  disabled?: boolean;
  mode?: 'signin' | 'signup';
}

export const isWebViewUserAgent = () => {
  if (typeof navigator === 'undefined') return false;
  return /wv|WebView|Android.*Version\/[0-9]\.[0-9]|iPhone.*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
};

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onCredential,
  disabled = false,
  mode = 'signin',
}) => {
  const { t, language } = useTranslation();
  const isWebView = isWebViewUserAgent();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (isWebView) return;

    const clientId = API_CONFIG.GOOGLE.CLIENT_ID;

    if (!clientId) {
      setLocalError(t('auth.googleClientMissing'));
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;

    let cancelled = false;

    const init = () => {
      if (!buttonRef.current || cancelled) {
        return;
      }

      const googleId = window.google?.accounts?.id;
      if (!googleId) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          setLocalError(t('auth.googleUnavailable'));
          return;
        }
        window.setTimeout(init, 250);
        return;
      }

      if (!initializedRef.current) {
        googleId.initialize({
          client_id: clientId,
          callback: async (response: GoogleCredentialResponse) => {
            if (!response.credential) {
              setLocalError(t('auth.googleAuthFailed'));
              return;
            }

            setLocalError(null);
            try {
              await onCredential(response.credential);
            } catch {
              setLocalError(t('auth.googleAuthFailed'));
            }
          },
          ux_mode: 'popup',
          context: mode === 'signup' ? 'signup' : 'signin',
        });
        initializedRef.current = true;
      }

      buttonRef.current.innerHTML = '';
      googleId.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: mode === 'signup' ? 'signup_with' : 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 320,
        locale: language,
      });
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [mode, onCredential, t, isWebView, language]);

  if (isWebView) {
    return null;
  }

  if (disabled) {
    return (
      <button type="button" className="google-signin-placeholder" disabled>
        {t('auth.loggingIn')}
      </button>
    );
  }

  return (
    <div className="google-signin-wrap">
      {localError && <div className="google-signin-error">{localError}</div>}
      <div ref={buttonRef} className="google-signin-button" />
    </div>
  );
};

export default GoogleSignInButton;
